import { memo } from "react";
import cx from "classnames";
import { useShallow } from "zustand/react/shallow";

import Button from "ds/components/Button";
import useIacManagementResources from "views/Account/IacManagement/useIacManagementResources";
import useIacManagementStore from "views/Account/IacManagement/useIacManagementStore";
import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { RESOURCES_ROWS_PER_PAGE } from "views/Account/IacManagement/ListLayout/constants";
import { IacManagementGroupBy } from "views/Account/IacManagement/types";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";

import IacManagementTableBodyRow from "..";
import useIacTableStore from "../../useIacTableStore";
import IacManagementTableRowServiceCell from "./Cell";
import styles from "../styles.module.css";
import { selectTableRowByIndex } from "../../useIacTableStore/tableDataSelectors";

type IacManagementTableRowServiceProps = {
  rowIndex: number;
  measureRef: (node: HTMLDivElement) => void;
};

const IacManagementTableRowService = ({
  rowIndex,
  measureRef,
}: IacManagementTableRowServiceProps) => {
  const { columns, row, loadResources, isFirstColumnPinned } = useIacTableStore(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      row: selectTableRowByIndex(state, rowIndex),
      loadResources: state.loadResources,
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { groupBy, parentStackId } = useIacManagementStore(
    useShallow((state) => ({
      groupBy: state.groupBy,
      parentStackId: state.parentStackId,
    }))
  );

  const { fetchData, loading } = useIacManagementResources();
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  const handleLoadMore = () => {
    if (!("groupId" in row)) {
      return;
    }

    loadResources(
      { groupId: row.groupId, groupBy, parentStackId, rowsPerPage: RESOURCES_ROWS_PER_PAGE },
      fetchData
    );
  };

  return (
    <IacManagementTableBodyRow
      {...keyboardProps}
      dataIndex={rowIndex}
      forwardRef={measureRef}
      className={cx(styles.serviceRow, {
        [styles.noneGroup]: groupBy === IacManagementGroupBy.None,
      })}
    >
      {columns.map((column, i) => {
        const cellId = `${row.id}-${column.id}-service-cell`;

        return (
          <IacManagementTableRowServiceCell
            key={cellId}
            id={cellId}
            stickyLeft={i === 0 && isFirstColumnPinned}
            stickyRight={i === columns.length - 1}
          >
            {i === 0 && (
              <Button variant="secondary" onPress={handleLoadMore} loading={loading} size="medium">
                Load {RESOURCES_ROWS_PER_PAGE} more resources
              </Button>
            )}
          </IacManagementTableRowServiceCell>
        );
      })}
    </IacManagementTableBodyRow>
  );
};

export default memo(IacManagementTableRowService);
